import { Schema } from 'effect';
import { GeometryShapeTypes, type IGeometryShapeType } from './geometry-shape-types';
import { PositiveInteger } from './shared';

/** Where the engine names the shapes, and what every one of their paths begins with. */
export const GEOMETRIES_ROOT = '/configurator/configuration-geometries';

/**
 * Which shape a geometry is, taken apart the way a side is.
 *
 * One literal per shape rather than {@link GeometryShapeTypes} as a whole, and that is the point: the shape is what the union below is told apart by, so each class may only decode its own. A parser rather than a plain literal, so the shape arrives already narrowed and a reader needs no cast to know which one it is.
 */
function shapeOf<const Type extends IGeometryShapeType>(type: Type) {
	return Schema.Struct({ self: Schema.TemplateLiteralParser([`${GEOMETRIES_ROOT}/`, Schema.Literal(type)]) });
}

/** Where {@link shapeOf} puts the shape once it has been taken apart. */
export const SHAPE_PART = 1;

/**
 * Which shape a geometry is, when it may be any of them.
 *
 * For a shape that is sent rather than read: the user picks it and the engine works out its measurements, so there is no shape class to tell it apart by, only the list of shapes there are. Taken apart the way {@link shapeOf} takes one apart, with the shape at {@link SHAPE_PART}.
 */
export const GeometryShapeSelf = Schema.TemplateLiteralParser([`${GEOMETRIES_ROOT}/`, GeometryShapeTypes]);

/**
 * The measurements the shapes share, each written once.
 *
 * Twenty-two shapes and only a handful of sets of measurements, so the sets are named here and spread into the shapes that have them. Whole millimetres and one or more, like the width and the height they are measured against.
 */
const leftChamfer = { leftHeight: PositiveInteger, leftWidth: PositiveInteger };
const rightChamfer = { rightHeight: PositiveInteger, rightWidth: PositiveInteger };
const leftSlope = { leftHeight: PositiveInteger };
const rightSlope = { rightHeight: PositiveInteger };
const leftRounding = { leftRadius: PositiveInteger };
const rightRounding = { rightRadius: PositiveInteger };
const arch = { rise: PositiveInteger };

/**
 * The code the engine writes beside every shape, whichever it is — `1A` for a rectangle.
 *
 * On every shape rather than on the parameter, because it is where the engine puts it: inside the value, next to the shape. It is what the answers list shows for a geometry, so an empty one is refused rather than shown as nothing.
 */
const vip = { vip: Schema.NonEmptyString };

export class RectangleShape extends Schema.Class<RectangleShape>('RectangleShape')(Schema.Struct({ shape: shapeOf('rectangle'), ...vip })) {}

export class CircleShape extends Schema.Class<CircleShape>('CircleShape')(Schema.Struct({ shape: shapeOf('circle'), ...vip })) {}

export class ArchTopShape extends Schema.Class<ArchTopShape>('ArchTopShape')(Schema.Struct({ ...arch, shape: shapeOf('arch-top'), ...vip })) {}

export class ChamferTopLeftShape extends Schema.Class<ChamferTopLeftShape>('ChamferTopLeftShape')(Schema.Struct({ ...leftChamfer, shape: shapeOf('chamfer-top-left'), ...vip })) {}

export class ChamferTopBothShape extends Schema.Class<ChamferTopBothShape>('ChamferTopBothShape')(Schema.Struct({ ...leftChamfer, ...rightChamfer, shape: shapeOf('chamfer-top-both'), ...vip })) {}

export class ChamferTopRightShape extends Schema.Class<ChamferTopRightShape>('ChamferTopRightShape')(Schema.Struct({ ...rightChamfer, shape: shapeOf('chamfer-top-right'), ...vip })) {}

export class ChamferBottomLeftShape extends Schema.Class<ChamferBottomLeftShape>('ChamferBottomLeftShape')(Schema.Struct({ ...leftChamfer, shape: shapeOf('chamfer-bottom-left'), ...vip })) {}

export class ChamferBottomBothShape extends Schema.Class<ChamferBottomBothShape>('ChamferBottomBothShape')(Schema.Struct({ ...leftChamfer, ...rightChamfer, shape: shapeOf('chamfer-bottom-both'), ...vip })) {}

export class ChamferBottomRightShape extends Schema.Class<ChamferBottomRightShape>('ChamferBottomRightShape')(Schema.Struct({ ...rightChamfer, shape: shapeOf('chamfer-bottom-right'), ...vip })) {}

export class SlopeTopLeftShape extends Schema.Class<SlopeTopLeftShape>('SlopeTopLeftShape')(Schema.Struct({ ...leftSlope, shape: shapeOf('slope-top-left'), ...vip })) {}

export class SlopeTopRightShape extends Schema.Class<SlopeTopRightShape>('SlopeTopRightShape')(Schema.Struct({ ...rightSlope, shape: shapeOf('slope-top-right'), ...vip })) {}

export class SlopeBottomLeftShape extends Schema.Class<SlopeBottomLeftShape>('SlopeBottomLeftShape')(Schema.Struct({ ...leftSlope, shape: shapeOf('slope-bottom-left'), ...vip })) {}

export class SlopeBottomRightShape extends Schema.Class<SlopeBottomRightShape>('SlopeBottomRightShape')(Schema.Struct({ ...rightSlope, shape: shapeOf('slope-bottom-right'), ...vip })) {}

export class TriangleTopLeftShape extends Schema.Class<TriangleTopLeftShape>('TriangleTopLeftShape')(Schema.Struct({ shape: shapeOf('triangle-top-left'), ...vip })) {}

export class TriangleTopBothShape extends Schema.Class<TriangleTopBothShape>('TriangleTopBothShape')(Schema.Struct({ shape: shapeOf('triangle-top-both'), ...vip })) {}

export class TriangleTopRightShape extends Schema.Class<TriangleTopRightShape>('TriangleTopRightShape')(Schema.Struct({ shape: shapeOf('triangle-top-right'), ...vip })) {}

export class TriangleBottomLeftShape extends Schema.Class<TriangleBottomLeftShape>('TriangleBottomLeftShape')(Schema.Struct({ shape: shapeOf('triangle-bottom-left'), ...vip })) {}

export class TriangleBottomBothShape extends Schema.Class<TriangleBottomBothShape>('TriangleBottomBothShape')(Schema.Struct({ shape: shapeOf('triangle-bottom-both'), ...vip })) {}

export class TriangleBottomRightShape extends Schema.Class<TriangleBottomRightShape>('TriangleBottomRightShape')(Schema.Struct({ shape: shapeOf('triangle-bottom-right'), ...vip })) {}

export class RoundedTopLeftShape extends Schema.Class<RoundedTopLeftShape>('RoundedTopLeftShape')(Schema.Struct({ ...leftRounding, shape: shapeOf('rounded-top-left'), ...vip })) {}

export class RoundedTopBothShape extends Schema.Class<RoundedTopBothShape>('RoundedTopBothShape')(Schema.Struct({ ...leftRounding, ...rightRounding, shape: shapeOf('rounded-top-both'), ...vip })) {}

export class RoundedTopRightShape extends Schema.Class<RoundedTopRightShape>('RoundedTopRightShape')(Schema.Struct({ ...rightRounding, shape: shapeOf('rounded-top-right'), ...vip })) {}

/**
 * A geometry in whichever of the shapes the engine has.
 *
 * One class per shape rather than one class with optional measurements, because a class says exactly which measurements its shape has: a shape missing one of them is refused rather than decoded with a hole in it. That is what lets {@link measurementsOf} read the measurements a shape has off the names it holds, and what
 * `instanceof` narrows to for a reader that needs one shape in particular.
 *
 * Told apart by `shape.self`, which each class may only decode its own of — see {@link shapeOf} — so the order here is the endpoint's and nothing depends on it.
 */
export const GeometryShapes = Schema.Union([
	RectangleShape,
	CircleShape,
	ArchTopShape,
	ChamferTopLeftShape,
	ChamferTopBothShape,
	ChamferTopRightShape,
	ChamferBottomLeftShape,
	ChamferBottomBothShape,
	ChamferBottomRightShape,
	SlopeTopLeftShape,
	SlopeTopRightShape,
	SlopeBottomLeftShape,
	SlopeBottomRightShape,
	TriangleTopLeftShape,
	TriangleTopBothShape,
	TriangleTopRightShape,
	TriangleBottomLeftShape,
	TriangleBottomBothShape,
	TriangleBottomRightShape,
	RoundedTopLeftShape,
	RoundedTopBothShape,
	RoundedTopRightShape,
]);

/** A geometry in whichever of the shapes the engine has. */
export type IGeometryShape = typeof GeometryShapes.Type;

/** The names of every member of a union together, which `keyof` on the union does not give: it gives only the names they all share. */
type KeysOfEach<T> = T extends unknown ? Extract<keyof T, string> : never;

/** One of the measurements a shape can have, by the name the engine gives it. */
export type IGeometryMeasurement = Exclude<KeysOfEach<IGeometryShape>, 'shape' | 'vip'>;

/**
 * Every measurement a shape can have, in the order the engine lists them: the width of a corner before its height, the left before the right.
 *
 * One order for all of them rather than one per shape, because the shapes agree: every shape lists the measurements it has in this order, so taking them out of this list in order is taking them in the engine's. The test beside this file holds the engine's list per shape, and is what notices the day the two drift.
 */
export const GEOMETRY_MEASUREMENTS = ['leftWidth', 'leftHeight', 'rightWidth', 'rightHeight', 'leftRadius', 'rightRadius', 'rise'] as const satisfies ReadonlyArray<IGeometryMeasurement>;

/** One measurement of a shape, and how long it is in whole millimetres. */
export interface IGeometryMeasured {
	readonly measurement: IGeometryMeasurement;
	readonly value: number;
}

/**
 * The measurements a shape has, in the order the engine lists them, each with how long it is.
 *
 * Read by name rather than by class. A decoded shape has exactly the measurements its class takes — a shape missing one is refused, see {@link GeometryShapes} — so the names it has are the answer, and twenty-two branches would only restate the classes. None is a shape with no measurements: a rectangle, a circle, a
 * triangle.
 */
export function measurementsOf(shape: IGeometryShape): ReadonlyArray<IGeometryMeasured> {
	// every shape is one of these: measurements it may or may not have, beside the vip they all do. Kept
	// apart from a bare Partial, which TypeScript refuses a shape with none of the measurements as
	const lengths: Partial<Record<IGeometryMeasurement, number>> & { readonly vip: string } = shape;
	return GEOMETRY_MEASUREMENTS.flatMap((measurement) => {
		const value = lengths[measurement];
		return value === undefined ? [] : [{ measurement, value }];
	});
}
